import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { FileText, Eye, Save, Shield } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const WaiversTab = () => {
  const [waiverEnabled, setWaiverEnabled] = useState(false);
  const [waiverText, setWaiverText] = useState('');
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [signedWaivers, setSignedWaivers] = useState([]);
  const [selectedWaiver, setSelectedWaiver] = useState(null);
  const [showWaiverDetails, setShowWaiverDetails] = useState(false);

  useEffect(() => {
    loadSettings();
    loadSignedWaivers();
  }, []);

  const loadSettings = () => {
    const enabled = localStorage.getItem('waiversEnabled') === 'true';
    const text = localStorage.getItem('orgWaiverText') || getDefaultWaiver();
    setWaiverEnabled(enabled);
    setWaiverText(text);
  };

  const loadSignedWaivers = async () => {
    try {
      const response = await api.get('/bookings');
      const waivers = response.data
        .filter(t => t.waiverSigned)
        .map(t => ({
          confirmationCode: t.confirmationCode || t.id.slice(0, 8),
          customerName: t.waiverRecord?.signatureName || t.customerName,
          method: t.waiverRecord?.verificationMethod || 'unknown',
          signedAt: t.waiverSignedAt,
          trip: t
        }));
      setSignedWaivers(waivers);
    } catch (error) {
      console.error('Error loading waivers:', error);
    }
  };

  const getDefaultWaiver = () => {
    return '';  // No default - operators must provide their own
  };

  const handleSave = () => {
    if (waiverEnabled && !waiverText.trim()) {
      toast.error('Waiver text is required when waivers are enabled');
      return;
    }

    setSaving(true);
    try {
      localStorage.setItem('waiversEnabled', waiverEnabled);
      localStorage.setItem('orgWaiverText', waiverText);
      toast.success('Waiver settings saved!');
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const isConfigured = waiverText.trim().length > 50;

  return (
    <div className="space-y-6">
      {/* Waiver Configuration */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Waiver System
            </CardTitle>
            {waiverEnabled && (
              <Badge className={isConfigured ? 'bg-green-500' : 'bg-orange-500'}>
                {isConfigured ? '✓ Configured' : '⚠️ Setup Required'}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Enable/Disable Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
            <div>
              <p className="font-semibold text-gray-900">Require Waivers for All Bookings</p>
              <p className="text-sm text-gray-600">Customers must sign before viewing trip details</p>
            </div>
            <button
              onClick={() => setWaiverEnabled(!waiverEnabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                waiverEnabled ? 'bg-blue-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  waiverEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Waiver Text Editor */}
          {waiverEnabled && (
            <>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="font-semibold">Custom Waiver Text</Label>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setShowPreview(true)}>
                      <Eye className="w-4 h-4 mr-1" />
                      Preview
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setWaiverText(getDefaultWaiver())}>
                      Reset to Default
                    </Button>
                  </div>
                </div>
                <Textarea
                  value={waiverText}
                  onChange={(e) => setWaiverText(e.target.value)}
                  placeholder="Enter your custom waiver text..."
                  className="min-h-[300px] font-mono text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {waiverText.length} characters
                </p>
              </div>

              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-900 font-medium mb-1">⚖️ Legal Disclaimer</p>
                <p className="text-xs text-blue-700">
                  CharterShare is NOT liable for the content or enforceability of your waiver. 
                  This is YOUR legal document. We strongly recommend consulting with a lawyer 
                  to ensure it meets your jurisdiction's requirements.
                </p>
              </div>
            </>
          )}

          {/* Save Button */}
          <div className="flex justify-end">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {saving ? 'Saving...' : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Settings
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Signed Waivers List */}
      {waiverEnabled && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Signed Waivers ({signedWaivers.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {signedWaivers.length > 0 ? (
              <div className="space-y-2">
                {signedWaivers.map((waiver, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border hover:bg-gray-100">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <span className="font-mono font-semibold text-blue-600">{waiver.confirmationCode}</span>
                        <span className="font-medium">{waiver.customerName || 'Unknown'}</span>
                        <Badge variant={waiver.method === 'sms' ? 'default' : 'secondary'}>
                          {waiver.method === 'sms' ? '✓ SMS' : waiver.method === 'teamMember_id_check' ? '📋 TeamMember ID' : 'Unknown'}
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(waiver.signedAt).toLocaleString()}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedWaiver(waiver.trip);
                        setShowWaiverDetails(true);
                      }}
                    >
                      View Details
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <FileText className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                <p>No waivers signed yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Preview Modal */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Waiver Preview</DialogTitle>
          </DialogHeader>
          <div className="mt-4 bg-gray-50 rounded-lg border p-4 text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
            {waiverText}
          </div>
          <Button onClick={() => setShowPreview(false)} className="w-full mt-4">
            Close
          </Button>
        </DialogContent>
      </Dialog>
      
      {/* Waiver Details Modal - Reuse from CalendarScheduler */}
      {selectedWaiver && (
        <Dialog open={showWaiverDetails} onOpenChange={setShowWaiverDetails}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Waiver Verification Details</DialogTitle>
            </DialogHeader>
            <div className="mt-4 space-y-3">
              <p><strong>Name:</strong> {selectedWaiver.waiverRecord?.signatureName}</p>
              <p><strong>Method:</strong> {selectedWaiver.waiverRecord?.verificationMethod}</p>
              <p><strong>Phone:</strong> {selectedWaiver.waiverRecord?.phoneNumber || 'N/A'}</p>
              <p><strong>IP:</strong> {selectedWaiver.waiverRecord?.ipAddress}</p>
              <p><strong>Signed:</strong> {new Date(selectedWaiver.waiverSignedAt).toLocaleString()}</p>
              <Button onClick={() => setShowWaiverDetails(false)} className="w-full mt-4">
                Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default WaiversTab;
